import { useState } from 'react';
import { toast } from '../components/Toast';
import { crmApi } from '../services/crmApi';
import type { CrmApplication, ApplicationEditData, EducationEntry, EmploymentEntry, CourseEntry } from '../types/crm';

interface EditDeps {
  id: string | undefined;
  application: CrmApplication | null;
  setSaving: (v: boolean) => void;
  loadApplication: () => Promise<void>;
}

export function useApplicationEdit({ id, application, setSaving, loadApplication }: EditDeps) {
  const [editMode, setEditMode] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [editedData, setEditedData] = useState<Record<string, any>>({});
  const [editedEducation, setEditedEducation] = useState<EducationEntry[]>([]);
  const [editedEmployment, setEditedEmployment] = useState<EmploymentEntry[]>([]);
  const [editedCourses, setEditedCourses] = useState<CourseEntry[]>([]);
  const [agreeToFoundationCourses, setAgreeToFoundationCourses] = useState(false);

  const handleEnterEditMode = () => {
    if (!application) return;
    setEditedData({
      first_name: application.first_name,
      last_name: application.last_name,
      employment_status: application.employment_status,
      has_ipd: application.has_ipd,
      no_previous_grants: application.no_previous_grants,
      no_work_refusal: application.no_work_refusal,
      phone: application.phone,
      email: application.email,
      pesel: application.pesel,
      residence_address: application.residence_address,
      registration_address: application.registration_address,
      marital_status: application.marital_status,
      property_regime: application.property_regime,
      spouse_consent: application.spouse_consent,
      business_idea_raw: application.business_idea_raw,
      pkd_code: application.pkd_code,
      industry: application.industry,
      business_type: application.business_type,
      premises_title: application.premises_title,
      premises_address: application.premises_address,
      ownership_document: application.ownership_document,
      loan_from_family: application.loan_from_family,
      loan_family_relation: application.loan_family_relation,
      has_rent_contract: application.has_rent_contract,
      has_business_registration_consent: application.has_business_registration_consent,
      other_company_exists: application.other_company_exists,
      other_company_pkd: application.other_company_pkd,
      other_company_nip: application.other_company_nip,
      landlord_consent: application.landlord_consent,
      contract_type: application.contract_type,
      requested_amount: application.requested_amount,
      security_form: application.security_form,
      guarantor_data: application.guarantor_data,
      is_education_related: application.is_education_related,
      is_location_in_krakow: application.is_location_in_krakow,
      is_dkr: application.is_dkr,
      is_over_50: application.is_over_50,
      is_under_30: application.is_under_30,
      is_disabled: application.is_disabled,
      is_long_term_unemployed: application.is_long_term_unemployed,
      is_single_parent: application.is_single_parent,
      has_no_qualifications: application.has_no_qualifications,
      operator_notes: application.operator_notes,
    });
    setEditedEducation(application.education || []);
    setEditedEmployment(application.employment || []);
    setEditedCourses(application.courses || []);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedData({});
  };

  const handleSaveChanges = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const fullUpdate = {
        ...editedData,
        education: editedEducation,
        employment: editedEmployment,
        courses: editedCourses,
      };
      await crmApi.updateApplication(parseInt(id), fullUpdate);
      try {
        await crmApi.recalculatePoints(parseInt(id));
      } catch (error) {
        console.error('Error recalculating points:', error);
      }
      toast.success('✅ Zmiany zapisane pomyślnie! Punkty przeliczone.');
      setEditMode(false);
      await loadApplication();
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('❌ Błąd podczas zapisywania zmian');
    } finally {
      setSaving(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEditFieldChange = (field: string, value: any) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  // Education
  const handleAddEducation = () => {
    setEditedEducation([...editedEducation, { school_name: '', education_level: '', field_of_study: '', completion_year: new Date().getFullYear() }]);
  };
  const handleRemoveEducation = (index: number) => {
    setEditedEducation(editedEducation.filter((_, i) => i !== index));
  };
  const handleEducationChange = (index: number, field: string, value: unknown) => {
    const updated = [...editedEducation];
    updated[index] = { ...updated[index], [field]: value };
    setEditedEducation(updated);
  };

  // Employment
  const handleAddEmployment = () => {
    setEditedEmployment([...editedEmployment, { company_name: '', company_nip: '', position: '', period_from: '', period_to: '', job_description: '' }]);
  };
  const handleRemoveEmployment = (index: number) => {
    setEditedEmployment(editedEmployment.filter((_, i) => i !== index));
  };
  const handleEmploymentChange = (index: number, field: string, value: unknown) => {
    const updated = [...editedEmployment];
    updated[index] = { ...updated[index], [field]: value };
    setEditedEmployment(updated);
  };

  // Courses
  const handleAddCourse = () => {
    setEditedCourses([...editedCourses, { course_name: '', has_certificate: false, completion_year: new Date().getFullYear() }]);
  };
  const handleRemoveCourse = (index: number) => {
    setEditedCourses(editedCourses.filter((_, i) => i !== index));
  };
  const handleCourseChange = (index: number, field: string, value: unknown) => {
    const updated = [...editedCourses];
    updated[index] = { ...updated[index], [field]: value };
    setEditedCourses(updated);
  };

  return {
    editMode, editedData, editedEducation, editedEmployment, editedCourses,
    agreeToFoundationCourses, setAgreeToFoundationCourses,
    handleEnterEditMode, handleCancelEdit, handleSaveChanges, handleEditFieldChange,
    handleAddEducation, handleRemoveEducation, handleEducationChange,
    handleAddEmployment, handleRemoveEmployment, handleEmploymentChange,
    handleAddCourse, handleRemoveCourse, handleCourseChange,
  };
}
